import React, { useId } from "react";
import { colors, fontSizes, radius, spacing, shadows } from "./tokens";
import ModalA11yWrapper from "./ModalA11yWrapper";
import Button from "./Button";

const sizeMap = {
  sm: 360,
  md: 520,
  lg: 720,
  xl: 900,
};

const headerBaseStyle = {
  padding: `${spacing.lg}px ${spacing.xl}px ${spacing.md}px`,
  borderBottom: `1px solid ${colors.border}`,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: spacing.md,
};

const titleStyle = {
  margin: 0,
  color: colors.text,
  fontSize: fontSizes.lg,
};

const descriptionStyle = {
  margin: `${spacing.xs}px 0 0 0`,
  color: colors.textMuted,
  fontSize: fontSizes.sm,
};

const bodyBaseStyle = {
  padding: `${spacing.lg}px ${spacing.xl}px`,
  color: colors.text,
};

const footerBaseStyle = {
  padding: `${spacing.md}px ${spacing.xl}px ${spacing.lg}px`,
  borderTop: `1px solid ${colors.border}`,
  display: "flex",
  justifyContent: "flex-end",
  gap: spacing.sm,
  background: colors.surfaceSubtle,
};

const closeButtonStyle = {
  minWidth: 36,
  minHeight: 36,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  color: colors.textMuted,
  fontSize: fontSizes.lg,
  lineHeight: 1,
  cursor: "pointer",
  padding: 0,
  boxShadow: shadows.xs,
};

function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  initialFocusRef,
  labelledBy: labelledByProp,
  describedBy: describedByProp,
  role = "dialog",
  size = "md",
  style,
  contentStyle,
  footerStyle,
}) {
  const internalId = useId();
  const titleId = title ? `${internalId}-title` : undefined;
  const descriptionId = description ? `${internalId}-description` : undefined;

  const labelledBy = labelledByProp || titleId;
  const describedBy = describedByProp || descriptionId;

  const width = typeof size === "number" ? size : sizeMap[size] || sizeMap.md;

  const containerStyle = {
    width: `min(90vw, ${width}px)`,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    color: colors.textPrimary,
    background: colors.surface,
    ...style,
  };

  return (
    <ModalA11yWrapper
      open={open}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
      labelledBy={labelledBy}
      describedBy={describedBy}
      role={role}
    >
      <div style={containerStyle}>
        {(title || description || onClose) && (
          <div style={headerBaseStyle}>
            <div>
              {title && (
                <h2 id={titleId} style={titleStyle}>
                  {title}
                </h2>
              )}
              {description && (
                <p id={descriptionId} style={descriptionStyle}>
                  {description}
                </p>
              )}
            </div>
            {onClose && (
              <Button type="button" onClick={onClose} aria-label="Close dialog" style={closeButtonStyle} variant="ghost">
                x
              </Button>
            )}
          </div>
        )}
        <div style={{ ...bodyBaseStyle, ...contentStyle }}>{children}</div>
        {footer && <div style={{ ...footerBaseStyle, ...footerStyle }}>{footer}</div>}
      </div>
    </ModalA11yWrapper>
  );
}

export default Modal;
